/** @jsx jsx */

import { Fragment } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

import Divider from 'app/developer-v3/components/Divider';

import {
  Button,
  Link as ZinniaLink,
  Spacer,
  Text,
  Tooltip,
  useCopyToClipboard,
} from '@zapier/design-system';

import { jsx, css } from '@emotion/core';
import { Icon } from '@zapier/zinnia-icons';

import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import Link from 'app/common/components/Link';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import {
  BASE_DOC_URL_VB,
  ZAPIER_APP_API_PATH,
} from 'app/developer-v3/constants';

const Styles = {
  description: css`
    margin-bottom: 20px;
  `,
  source: css`
    text-align: right;
  `,
  toggleWrapper: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
  `,
};

type Props = {
  definitionString: string;
  integrationId: number;
  integrationVersion: string;
  isGUIIntegration: boolean;
  params: { version: string };
};

/**
 * Displays the definition_override JSON of the current version.
 * Also, allows the user to copy the object-string to their clipboard.
 */
export function Source({
  definitionString,
  integrationId,
  integrationVersion,
  isGUIIntegration,
  params,
}: Props) {
  const [didCopy, handleCopy] = useCopyToClipboard(definitionString);

  return (
    <ManagerLayout title={`Source Definition for v${params.version}`}>
      {isGUIIntegration ? (
        <Fragment>
          <div css={Styles.description}>
            <Text margin="15px 0 0" tag="p">
              This is the source definition of the selected version of your
              integration. Copy this into your favorite diff tool to compare
              changes from a previous version.
            </Text>
            <Text margin="15px 0 0" tag="p">
              To learn more about this structure see the{' '}
              <Link
                href="https://github.com/zapier/zapier-platform/blob/master/packages/schema/docs/build/schema.md"
                target="_blank"
              >
                schema definition
              </Link>
              .
            </Text>
            <Text margin="15px 0 0" tag="p">
              Want to edit this code directly instead of working through the UI?
              Have a look at the{' '}
              <Link
                href={`${BASE_DOC_URL_VB}/build-cli/overview`}
                target="_blank"
              >
                Zapier CLI
              </Link>
              !
            </Text>
          </div>
          <section css={Styles.source}>
            <SyntaxHighlighter
              customStyle={{ height: '65vh' }}
              language="json"
              wrapLongLines={true}
            >
              {definitionString}
            </SyntaxHighlighter>
            <Tooltip
              content="Copied to clipboard!"
              placement="top"
              isOpen={didCopy}
            >
              <Button onPress={handleCopy}>Copy</Button>
            </Tooltip>
          </section>
        </Fragment>
      ) : (
        <Fragment>
          <div css={Styles.toggleWrapper} data-testid="source-zip-file">
            <div>
              <Text margin="0 8px 0 0" type="Body4">
                Source ZIP file
              </Text>
              <Text margin="10px 0px 0px" tag="p">
                Download the source code ZIP file for version{' '}
                {integrationVersion} of the integration.
              </Text>
              <Text margin="10px 0px 0px" tag="p">
                This is only available for CLI integrations.
              </Text>
            </div>
            <ZinniaLink
              isDisabled={isGUIIntegration}
              variant="primary"
              href={`${ZAPIER_APP_BASE_URL}${ZAPIER_APP_API_PATH}/${integrationId}/versions/${integrationVersion}/download`}
              iconPosition="start"
              target="_blank"
            >
              <Icon name="actionDownload" />
              Download
            </ZinniaLink>
          </div>
          <Spacer height={32} />
          <Divider />
        </Fragment>
      )}
    </ManagerLayout>
  );
}
